import {Clip} from "../clip";
import type {MediaClipProps} from './media.interfaces';
import {serializable} from "../../services";
import {Timestamp} from "../../models";
import {type float, frame} from "../../types";
import {RangeDeserializer} from "./media.deserializer";
import {AudioMixer} from "../../media/audio-mixer";
import {CompositionEventsEnum} from "../../../librettoEditor/CompositionEvents";
import {EngineInternalEventsEnum} from "../../EngineInternalEvents";
import type { AudioTransitionType } from "../script/script.interfaces";

/** Audio fade configuration for clip edges */
export interface ClipFadeConfig {
    type: AudioTransitionType;  // 'none' | 'microfade' | 'fade' | 'crossfade'
    durationMs: number;
}

// Visual fade configuration for clip edges (video/image).
export type ClipVisualTransitionType = 'none' | 'fade' | 'crossfade' | 'wipe' | 'blur';
export type ClipVisualWipeDirection = 'up' | 'down' | 'left' | 'right';

export interface ClipVisualFadeConfig {
    type: ClipVisualTransitionType;
    durationMs: number;
    wipeDirection?: ClipVisualWipeDirection;
    blurAmount?: number;
}

/** Internal transition point (soft split) within a clip */
export interface ClipTransitionPoint {
    id: string;
    /** Position in source time (seconds) */
    srcTimeSec: number;
    /** Transition configuration at this point (fadeOut = left side, fadeIn = right side) */
    transition?: {
        fadeOut?: ClipFadeConfig;  // End of segment A (left of the point)
        fadeIn?: ClipFadeConfig;   // Start of segment B (right of the point)
    };
}


export class MediaClip<Props extends MediaClipProps = MediaClipProps> extends Clip<MediaClipProps> {
    @serializable(Timestamp)
    public _offset = new Timestamp();

    public playing: boolean = false;

    @serializable(Timestamp)
    public readonly duration: Timestamp = new Timestamp();

    public _playbackRate: float = 1.0;

    public get playbackRate(): float {
        return this._playbackRate;
    }

    public set playbackRate(value: float) {
        const newRate = Math.max(0.1, value || 1);
        if (this._playbackRate === newRate) return;
        this._playbackRate = newRate;
        // Recompute any dependent derived values
        this.normalizeCuts();
        this.deriveSegments();
        // Notify render/UI
        this.trigger('frame', undefined);
        this.track?.composition.trigger(CompositionEventsEnum.CLIP_UPDATED, { clip: this });
        this.track?.composition.trigger(EngineInternalEventsEnum.CLIP_UPDATED, { clip: this });
        try {
            // force schedule rebuild + feeder/seek realign
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
    }

    /**
     * Trimmed start and stop values
     */
    @serializable(RangeDeserializer)
    public range: [Timestamp, Timestamp] = [new Timestamp(), this.duration];

    public constructor(props: MediaClipProps = {}) {
        super();

        Object.assign(this, props);
        // Initialize canonical cuts and derived segments
        this.normalizeCuts();
        this.deriveSegments();
    }

    // Gain as getter/setter to allow override in subclasses (e.g., ScriptLayerClip)
    protected _gain: number = 1.0;

    public get gain(): number {
        return this._gain;
    }

    public set gain(v: number) {
        this._gain = Math.max(0, v);
    }

    // ============ Transitions ============
    
    /** Fade-in configuration at the clip start edge */
    public fadeIn?: ClipFadeConfig;
    
    /** Fade-out configuration at the clip end edge */
    public fadeOut?: ClipFadeConfig;
    
    /** Visual fade-in configuration at the clip start edge (video/image). */
    public visualFadeIn?: ClipVisualFadeConfig;

    /** Visual fade-out configuration at the clip end edge (video/image). */
    public visualFadeOut?: ClipVisualFadeConfig;

    /** Internal transition points (soft splits) - positions where transitions can be configured */
    public transitionPoints: ClipTransitionPoint[] = [];

    // Duration after outer trims (continuous span)
    public get trimmedDuration(): Timestamp {
        return this.stop.subtract(this.start);
    }

    // Canonical internal edit state: source-time cuts inside [range[0], range[1]]
    public cuts: Array<{ id: string; startSec: number; endSec: number; groupId?: string }> = [];

    // Derived playable segments packed contiguously on the timeline
    private _segments: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = [];

    public get segments(): Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> {
        this.deriveSegments();
        return this._segments;
    }

    // Normalize cuts to be within outer trim, sorted, merged
    public normalizeCuts(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? inSec;
        const EPS = 1e-3;
        if (!Array.isArray(this.cuts)) {
            this.cuts = [];
            return;
        }
        let cs = this.cuts
            .map(c => ({
                id: c.id,
                startSec: Math.max(inSec, Math.min(outSec, c.startSec)),
                endSec: Math.max(inSec, Math.min(outSec, c.endSec)),
                groupId: c.groupId,
            }))
            .filter(c => c.endSec - c.startSec > EPS);
        cs.sort((a, b) => a.startSec - b.startSec);
        const merged: typeof cs = [];
        for (const c of cs) {
            if (!merged.length) { merged.push({ ...c }); continue; }
            const last = merged[merged.length - 1];
            if (c.startSec <= last.endSec + EPS) {
                last.endSec = Math.max(last.endSec, c.endSec);
                // groupId remains from the first cut in the merged span
            } else {
                merged.push({ ...c });
            }
        }
        this.cuts = merged;
    }

    // Derive ordered segments = (outer span minus cuts), packed from clip.start
    public deriveSegments(): void {
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? inSec;
        const baseCompStart = this.start?.seconds ?? (this.offset?.seconds ?? 0) + inSec;
        const spans: Array<{ srcStartSec: number; srcEndSec: number }> = [];
        let cursor = inSec;
        for (const cut of this.cuts) {
            const s = Math.max(inSec, Math.min(outSec, cut.startSec));
            const e = Math.max(inSec, Math.min(outSec, cut.endSec));
            if (s > cursor) spans.push({ srcStartSec: cursor, srcEndSec: s });
            cursor = Math.max(cursor, e);
        }
        if (cursor < outSec) spans.push({ srcStartSec: cursor, srcEndSec: outSec });

        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = [];
        let compCursor = baseCompStart;
        for (const sp of spans) {
            const segDur = Math.max(0, sp.srcEndSec - sp.srcStartSec);
            if (segDur <= 0) continue;
            segs.push({ srcStartSec: sp.srcStartSec, srcEndSec: sp.srcEndSec, compStartSec: compCursor });
            compCursor += segDur;
        }
        this._segments = segs;
    }

    // Timeline duration from segments (fallback to trimmedDuration)
    public get timelineDuration(): Timestamp {
        if (!this.segments || this.segments.length === 0) {
            const durationMillis = this.trimmedDuration.millis / this.playbackRate;
            return Timestamp.fromMillis(durationMillis);
        }
        const first = this.segments[0];
        const last = this.segments[this.segments.length - 1];
        const lastEndComp = last.compStartSec + Math.max(0, last.srcEndSec - last.srcStartSec);
        const durationSecs = Math.max(0, lastEndComp - first.compStartSec) / this.playbackRate;
        return Timestamp.fromSeconds(durationSecs);
    }

    public VisibleCutToSourceCuts(trimStartSec: number, trimEndSec: number) {
        this.deriveSegments();

        // The first visible cut maps to the same source cut as there's no segments. (I.E. no trims yet)
        if (!this.segments || this.segments.length === 0) return {
            srcStartSec: trimStartSec,
            srcEndSec: trimEndSec
        };

        if (trimEndSec < trimStartSec) [trimStartSec, trimEndSec] = [trimEndSec, trimStartSec];

        const trimStartInSourceTime = this.CompositionTimeToClipTime(trimStartSec);
        const trimEndInSourceTime = this.CompositionTimeToClipTime(trimEndSec);

        return {srcStartSec: trimStartInSourceTime, srcEndSec: trimEndInSourceTime};
    }

    public CompositionTimeToClipTime(compSec: number) {
        const rate = this.playbackRate ?? 1;
        const segs = this.segments ?? [];

        if (Array.isArray(segs) && segs.length > 0) {
            for (const seg of segs) {
                const segDur = Math.max(0, (seg.srcEndSec ?? 0) - (seg.srcStartSec ?? 0));
                const segStartInCompositionTime = seg.compStartSec ?? 0;
                const segEndInCompositionTime = segStartInCompositionTime + segDur;
                if (compSec >= segStartInCompositionTime && compSec < segEndInCompositionTime) {
                    const intoSeg = compSec - segStartInCompositionTime;
                    return (seg.srcStartSec ?? 0) + intoSeg * rate;
                }
            }
            // Outside all segments: snap to nearest boundary
            const first = segs[0];
            const last = segs[segs.length - 1];
            if (compSec < (first.compStartSec ?? 0)) return first.srcStartSec ?? 0;
            return last.srcEndSec ?? (first.srcStartSec ?? 0);
        }

        // Fallback: continuous span (no internal cuts)
        const inPointSec = this.range?.[0]?.seconds ?? 0;
        const offsetSec = this.offset?.seconds ?? 0;
        const startSec = offsetSec + inPointSec; // clip.start on timeline
        const rel = Math.max(0, compSec - startSec);
        return inPointSec + rel * rate;
    }

    public get start(): Timestamp {
        return this.range[0].add(this.offset);
    }

    public get stop(): Timestamp {
        return this.range[1].add(this.offset);
    }

    public set start(time: frame | Timestamp) {
        if (typeof time == 'number') {
            time = Timestamp.fromFrames(time);
        }

        const delta = time.subtract(this.offset);

        // Bounds for audio/video clips:
        // - range[0] can be 0 at minimum (start of source)
        // - range[0] must be less than range[1] (can't go past the end)
        if (delta.millis >= 0 && delta.millis < this.range[1].millis) {
            // case in between valid range
            this.range[0].millis = delta.millis;
        } else if (delta.millis < 0) {
            // case lower than min value - clamp to source start
            this.range[0].millis = 0;
        } else {
            // case larger than max value - clamp to just before end
            this.range[0].millis = this.range[1].millis - 1;
        }

        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        // Inform composition so audio schedule/feeders get updated when playing
        try {
            // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch { /* noop */ }
    }

    public set stop(time: frame | Timestamp) {
        if (typeof time == 'number') {
            time = Timestamp.fromFrames(time);
        }

        const delta = time.subtract(this.offset);

        // Bounds for audio/video clips:
        // - range[1] must be greater than range[0] (can't go before start)
        // - range[1] can be at most duration (end of source media)
        if (delta.millis > this.range[0].millis && delta.millis <= this.duration.millis) {
            // case in between valid range
            this.range[1] = delta;
        } else if (delta.millis > this.duration.millis) {
            // case larger than max value - clamp to source duration
            this.range[1] = this.duration;
        } else {
            // case lower than min value - clamp to just after start
            this.range[1].millis = this.range[0].millis + 1;
        }

        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        // Inform composition so audio schedule/feeders get updated when playing
        try {
            // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch { /* noop */ }
    }

    /**
     * Offest from frame 0 of the composition
     */
    public get offset(): Timestamp {
        return this._offset;
    }

    /**
     * Change clip's offset on the composition timeline. This moves the clip without
     * altering its in/out trim (range).
     */
    public set offset(time: frame | Timestamp) {
        if (typeof time == 'number') {
            // Treat numbers as frames
            this._offset.frames = time;
        } else {
            this._offset = time;
        }
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', this._offset.frames);
        // Inform composition so audio schedule/feeders get updated when playing
        try {
            // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch { /* noop */ }
    }

    // Cut mutation helpers
    public setCuts(cuts: Array<{ startSec: number; endSec: number; groupId?: string }>) {
        // Auto-generate ids for incoming cuts
        this.cuts = Array.isArray(cuts)
            ? cuts.map(c => ({ id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`, startSec: c.startSec, endSec: c.endSec, groupId: c.groupId }))
            : [];
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        try { // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
    }

    public addCut(cut: { startSec: number; endSec: number; groupId?: string }) {
        const cutId = crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`;
        this.cuts.push({ id: cutId, startSec: cut.startSec, endSec: cut.endSec, groupId: cut.groupId });
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        try { // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
        return cutId;
    }

    public removeCutAt(index: number) {
        if (index < 0 || index >= this.cuts.length) return;
        this.cuts.splice(index, 1);
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        try { // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
    }

    public removeCutById(cutId: string) {
        const idx = this.cuts.findIndex(c => c.id === cutId);
        if (idx === -1) return;
        this.cuts.splice(idx, 1);
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        try { // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
    }

    public removeCutsByGroup(groupId: string) {
        if (!groupId) return;
        this.cuts = this.cuts.filter(c => c.groupId !== groupId);
        this.normalizeCuts();
        this.deriveSegments();
        this.trigger('frame', undefined);
        try { // @ts-ignore
            this.track?.composition?.onClipTimingChanged?.();
        } catch {}
    }

    // ============ Transition Point Management ============
    
    /**
     * Add a transition point (soft split) at the given source time.
     * Does NOT split the clip - just marks a point where a transition can be configured.
     */
    public addTransitionPoint(
        srcTimeSec: number, 
        transition?: { fadeOut?: ClipFadeConfig; fadeIn?: ClipFadeConfig }
    ): string {
        const id = crypto?.randomUUID?.() ?? `tp_${Date.now()}_${Math.random()}`;
        
        // Clamp to valid range
        const inSec = this.range?.[0]?.seconds ?? 0;
        const outSec = this.range?.[1]?.seconds ?? inSec;
        const clampedTime = Math.max(inSec, Math.min(outSec, srcTimeSec));
        
        this.transitionPoints.push({
            id,
            srcTimeSec: clampedTime,
            transition,
        });
        
        // Sort by time
        this.transitionPoints.sort((a, b) => a.srcTimeSec - b.srcTimeSec);
        
        this.trigger('frame', undefined);
        this.track?.composition.trigger(CompositionEventsEnum.CLIP_UPDATED, { clip: this });
        
        return id;
    }
    
    /**
     * Remove a transition point by ID.
     */
    public removeTransitionPoint(id: string): void {
        const idx = this.transitionPoints.findIndex(tp => tp.id === id);
        if (idx === -1) return;
        
        this.transitionPoints.splice(idx, 1);
        this.trigger('frame', undefined);
        this.track?.composition.trigger(CompositionEventsEnum.CLIP_UPDATED, { clip: this });
    }
    
    /**
     * Update the transition configuration for a transition point.
     */
    public setTransitionPointConfig(
        id: string, 
        transition: { fadeOut?: ClipFadeConfig; fadeIn?: ClipFadeConfig }
    ): void {
        const tp = this.transitionPoints.find(t => t.id === id);
        if (!tp) return;
        
        tp.transition = transition;
        this.trigger('frame', undefined);
        this.track?.composition.trigger(CompositionEventsEnum.CLIP_UPDATED, { clip: this });
    }
    
    /**
     * Convert a transition point into a hard split (two separate clips).
     * Returns the new clip created to the right of the split.
     */
    public separateAtTransitionPoint(id: string): MediaClip | null {
        const tp = this.transitionPoints.find(t => t.id === id);
        if (!tp) return null;
        
        // Remove the transition point since we're converting it to a hard split
        this.removeTransitionPoint(id);
        
        // The actual split logic is handled by CompositionService.splitClip
        // This method just marks where the split should happen
        return null; // Actual split handled externally
    }
    
    /**
     * Get the transition point nearest to a given source time within a threshold.
     */
    public getTransitionPointNear(srcTimeSec: number, thresholdSec: number = 0.1): ClipTransitionPoint | null {
        let nearest: ClipTransitionPoint | null = null;
        let minDist = thresholdSec;
        
        for (const tp of this.transitionPoints) {
            const dist = Math.abs(tp.srcTimeSec - srcTimeSec);
            if (dist < minDist) {
                minDist = dist;
                nearest = tp;
            }
        }
        
        return nearest;
    }

    public async prepareForPlayback(audioCtx: AudioContext, _masterGain: GainNode, mixer?: AudioMixer) {

    }

    public stopAudio() {

    }


}
